<template lang="pug">
  .bubbleChart
    highcharts(
      :options="options"
      :updateArgs="updateArgs"
      ref="chart"
    )
</template>

<script>
import { Chart } from 'highcharts-vue'
import Highcharts from 'highcharts'
import More from 'highcharts/highcharts-more'
More(Highcharts)

export default {
  name: 'BubbleChart',
  props: {
    data: {},
    title: {},
    height: {},
    width: {},
    settings: {},
    exchangeRate: {
      default: 1
    }
  },
  data: function () {
    return {
      options: {},
      updateArgs: [true, true, { duration: 1000 }]

    }
  },
  components: {
    highcharts: Chart
  },
  watch: {
    data: {
      deep: true,
      handler () {
        this.$refs.chart.chart.series[0].setData(this.chartData)
        this.options = this.chartOptions()
      }
    },
    settings: {
      deep: true,
      handler () {
        this.options = this.chartOptions()
      }
    }
  },
  computed: {
    chartData () {
      return this.filteredData().map(value => {
        return {
          x: Math.round(value[this.settings.x.apiLocation][this.settings.x.item] * this.settings.x.multiplier, 0),
          y: this.round(value[this.settings.y.apiLocation][this.settings.y.item] * this.settings.y.multiplier, this.settings.y.precision),
          z: Math.round(value[this.settings.z.apiLocation][this.settings.z.item] * this.settings.z.multiplier, 0),
          name: value.model.assetName,
          xLabel: this.settings.x.label,
          yLabel: this.settings.y.label,
          zLabel: this.settings.z.label
        }
      })
    },
    marketCapData () {
      return this.filteredData().map(value => {
        return {
          x: Math.round(value[this.settings.x.apiLocation][this.settings.x.item] * this.settings.x.multiplier, 0),
          y: this.round(value[this.settings.y.apiLocation][this.settings.y.item] * this.settings.y.multiplier, this.settings.y.precision),
          z: Math.round(value.owner.market_cap * this.exchangeRate, 0),
          o: Math.round(value[this.settings.z.apiLocation][this.settings.z.item] * this.settings.z.multiplier, 0),
          name: value.owner.name,
          xLabel: this.settings.x.label,
          yLabel: this.settings.y.label,
          zLabel: 'Market Cap',
          oLabel: this.settings.z.label
        }
      })
    },
    data_x_max () {
      return Math.max(...this.chartData.map(value => { return value.x }))
    },
    data_y_max () {
      return Math.max(...this.chartData.map(value => { return value.y }))
    },
    data_z_max () {
      return Math.max(...this.chartData.map(value => { return value.z }))
    },
    x_max () {
      return Math.max(this.settings.x.max, this.data_x_max * 1.10)
    },
    y_max () {
      return Math.max(this.settings.y.max, this.data_y_max * 1.10)
    },
    z_max () {
      return Math.max(this.settings.z.max, this.data_z_max * 1.10)
    },
    data_x_min () {
      return Math.min(...this.chartData.map(value => { return value.x }))
    },
    data_y_min () {
      return Math.min(...this.chartData.map(value => { return value.y }))
    },
    data_z_min () {
      return Math.min(...this.chartData.map(value => { return value.z }))
    },
    x_min () {
      return Math.min(this.settings.x.min, this.data_x_min / 1.10)
    },
    y_min () {
      return Math.min(this.settings.y.min, this.data_y_min / 1.10)
    },
    z_min () {
      return Math.min(this.settings.z.min, this.data_z_min / 1.10)
    }
  },
  methods: {
    filteredData () {
      let data = this.data.filter(value => { return value.valuation.irr !== 'N/A' })
      return data.filter(value => { return value.lom.AISC !== 0 })
    },
    round (number, decimals) {
      return (
        Math.round(number * Math.pow(10, decimals)) / Math.pow(10, decimals)
      )
    },
    chartOptions () {
      return {
        chart: {
          colors: ['#a8bfac', '#cedbb4', '#b8d9eb'],
          width: this.width,
          height: this.height,
          animation: true
        },
        credits: {
          enabled: false
        },
        title: {
          text: this.settings.title
        },
        subtitle: {
          text: this.settings.subtitle
        },
        legend: {
          enabled: true
        },
        tooltip: {
          useHTML: true
        },
        plotOptions: {
          bubble: {
            sizeByAbsoluteValue: true
          }
        },
        xAxis: {
          title: { text: this.settings.x.axisLabel },
          min: this.x_min,
          max: this.x_max
        },
        yAxis: {
          title: { text: this.settings.y.axisLabel },
          min: this.y_min,
          max: this.y_max
        },
        zAxis: {
          title: { text: this.settings.z.axisLabel },
          min: this.z_min,
          max: this.z_max
        },
        series: [{
          name: this.settings.z.axisLabel,
          dataLabels: {
            enabled: true,
            format: '{point.name}'
          },
          tooltip: {
            headerFormat: '<table>',
            pointFormat: '<tr><th colspan="2"><h3>{point.name}</h3></th></tr>' +
              '<tr><th>{point.xLabel}:</th><td>{point.x}</td></tr>' +
              '<tr><th>{point.yLabel}:</th><td>{point.y}</td></tr>' +
              '<tr><th>{point.zLabel}:</th><td>{point.z}</td></tr>',
            footerFormat: '</table>',
            followPointer: true
          },
          type: 'bubble',
          data: this.chartData,
          marker: {
            fillColor: {
              radialGradient: { cx: 0.4, cy: 0.3, r: 0.7 },
              stops: [
                [0, 'rgba(255,255,255,0.5)'],
                [1, Highcharts.Color(Highcharts.getOptions().colors[0]).setOpacity(0.5).get('rgba')]
              ]
            }
          }
        },
        {
          type: 'bubble',
          name: 'Owner Market Cap',
          dataLabels: {
            enabled: true,
            format: '{point.name}'
          },
          tooltip: {
            headerFormat: '<table>',
            pointFormat: '<tr><th colspan="2"><h3>{point.name}</h3></th></tr>' +
              '<tr><th>{point.xLabel}:</th><td>{point.x}</td></tr>' +
              '<tr><th>{point.yLabel}:</th><td>{point.y}</td></tr>' +
              '<tr><th>{point.oLabel}:</th><td>{point.o}</td></tr>' +
              '<tr><th>{point.zLabel}:</th><td>{point.z}</td></tr>',
            footerFormat: '</table>',
            followPointer: true
          },
          data: this.marketCapData,
          marker: {
            fillColor: {
              radialGradient: { cx: 0.4, cy: 0.3, r: 0.7 },
              stops: [
                [0, 'rgba(255,255,255,0.5)'],
                [1, Highcharts.Color(Highcharts.getOptions().colors[2]).setOpacity(0.5).get('rgba')]
              ]
            }
          }
        }
        ]
      }
    }
  },
  created () {
    this.options = this.chartOptions()
  },
  mounted () {
    this.options = this.chartOptions()
  }
}
</script>
